import React, { useState, useMemo, useContext, useCallback, useEffect, useRef } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { AdTypeEnum } from '@bingads-webui-campaign-react/image-experience-model';
import { ImageExperience, getDefaultCurrentSelectedImageId, getDefaultSelectedImageId } from '@bingads-webui-campaign-react/image-experience';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import odata from '@bingads-webui-component-legacy/http/channel/odata';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withPreference } from '@bingads-webui-react/preference-context';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { instrumentationScenarios } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';
import { MediaRepository } from '@bingads-webui-campaign/media-repository';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { validateAdUrl } from '@bingads-webui-campaign-react/url-input-fluent';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { useAssetLibraryEventHandler, AssetPropertyName } from '@bingads-webui-campaign-react/copilot-common';

import { getStyles } from './style';
import { getInternalFinalUrlStateFromUrl, getImageListFromVm, getAssetsFromVm, createImagesVm, getImageAssetsFromVm, pmaxRequiredAspectRatios, audienceMMAV2RequiredAspectRatios, specialMinimumSizeReqiurementAspectRatios, adPreviewConfig as PMAXAdPreviewConfig, adPreviewConfigV2 as PMaxAdPreviewConfigV2, adPreviewConfigV3 as PMaxAdPreviewConfigV3 } from './shared';
import { getODataConfig, processImageListModelToViewModel } from './image-list-data';

export const PMaxImageExperienceMaxImageCount = 20;

const ImageExperienceWrapperInternal = (props) => {
  const {
    validationProps,
    updateValidation,
    imageAssetsOptional,
    isLogoEnabled,
    isBoostPortraitEnabled,
    updateFinalUrlCallback,
    updateImageListCallback,
    i18n,
    permissions,
    preferenceService,
    imagesContaineRef,
    logosContaineRef,
    portraitContainerRef,
    campaignId,
    previewImagesSharedState,
    setPreviewImagesSharedState,
    updatePortalContainerState,
    setImageExperienceAddNewImageCallback,
    suggestionPoller,
    wizardPerfMarker,
    postAssetEventLog,
    isCombinedMsanAndMmaAspectRatios,
    customMaxImageCount,
    UAPLPMaxIntegration,
    isMSANBoostEnabled,
    imageListPropName,
    isBulkEditMMA,
    isImproveMMA,
    genericImage,
    isMultimediaAd,
    isMultimediaAdinMSANEnabled,
    isCopilotRecommendations,
    adPreviewConfig,
    optionalAspectRatio,
    isStoreAppProng,
    isStoreAppSpotLight,
    isAd,
    isStockImageEnabled,
    isStoreAppTransparentLogo,
    isMMAV2Enabled,
    showCopilotOptionForImage,
    customAssetLibraryEventHandler,
    isLinkedInAd,
    customPreviewTexts,
    customOnEditImage,
    onChangeEditImageId,
    customOnSaveEditImage,
    onRenderAllowedAspectRatioControl,
    isAudienceAd,
    supportImageUpscale,
    isImageFittingEnabled,
    isUnifiedAppCampaignWindowsStore,
    isPMaxAspectRatiosV2Enabled,
    isAdPreviewRefreshEnabled,
    onToggleAspectRatio,
    canExcludeAspectRatio,
    includedTypeList,
    newI18n,
    previewTexts,
  } = props;
  if (adPreviewConfig.placementConfig === undefined) {
    const refreshedAdPreviewConfig = isAdPreviewRefreshEnabled ? PMaxAdPreviewConfigV3 : PMaxAdPreviewConfigV2;
    adPreviewConfig.placementConfig = isPMaxAspectRatiosV2Enabled ? refreshedAdPreviewConfig : PMAXAdPreviewConfig;
  }

  const [recommendationContext, , reportRecommendations] = useContext(RecommendationContext);
  const { copilotTracking } = recommendationContext;

  const [finalUrlState, setFinalUrlState] = useState({
    wizardFinalUrl: null,
    internalFinalUrl: null,
    internalFinalUrlProtocol: null,
    internalFinalUrlDest: null,
  });

  const [state, setState] = useState({
    thumbnailRecord: null,
    currentSelectedImageId: null,
    currentSelectedLogoId: null,
  });

  const imageExperienceAddImageCalbackRef = useRef(_.noop);
  const suggestedImageBeingAdded = useRef(null);

  useEffect(() => {
    setImageExperienceAddNewImageCallback(() => (images, imageSource, replaceExistingAssets) => {
      const { fullUrl, suggestionId } = images[images.length - 1];
      if (images.length === 1) {
        suggestedImageBeingAdded.current = { fullUrl, suggestionId };
      }
      if (isCopilotRecommendations) {
        return imageExperienceAddImageCalbackRef.current(images, imageSource, replaceExistingAssets);
      }
      return imageExperienceAddImageCalbackRef.current(fullUrl, imageSource, suggestionId, replaceExistingAssets);
    });
  }, [isCopilotRecommendations, setImageExperienceAddNewImageCallback]);

  const classes = useDefaultStyles(getStyles);
  const serverContext = useContext(ServerContext);
  const { scope } = useContext(ScopeContext);

  const activity = useContext(ActivityContext);

  const isMeetingMinimumRequirementPerAspectRatio = useCallback((imageList, forLogos) => {
    let requiredAspectRatios = pmaxRequiredAspectRatios;
    if (isMMAV2Enabled) {
      requiredAspectRatios = audienceMMAV2RequiredAspectRatios;
    }
    if (isLinkedInAd) {
      return imageList && imageList.length > 0;
    }
    const ratioCounts = _.mapObject(optionalAspectRatio || requiredAspectRatios, () => 0);
    _.each(imageList, imageArray =>
      _.each(imageArray, (imageObj) => {
        if (imageObj.isIncluded && imageObj.isLogo === forLogos && !imageObj.isPortrait && _.has(requiredAspectRatios, imageObj.type)) {
          ratioCounts[imageObj.type] += 1;
        }

        if (isMMAV2Enabled && _.has(specialMinimumSizeReqiurementAspectRatios, imageObj.type) && imageObj.isLogo === false && !imageObj.isPortrait) {
          // for Image169x100, the minimum size (703x372) is bigger than original image (required) 703x368
          const minimumSizerequirement = specialMinimumSizeReqiurementAspectRatios[imageObj.type];
          const imageActualCroppedSize = imageObj.cropSettings || {};
          const isThisAspectRatioExcludedForMinimumSizeReqiurement = imageActualCroppedSize.SourceWidth < minimumSizerequirement[0] || imageActualCroppedSize.SourceHeight < minimumSizerequirement[1];
          ratioCounts[imageObj.type] += isThisAspectRatioExcludedForMinimumSizeReqiurement ? 1 : 0;
        }
      }));
    return _.every(requiredAspectRatios, (minCount, type) => ratioCounts[type] >= minCount);
  }, [isLinkedInAd, isMMAV2Enabled, optionalAspectRatio]);

  const validateImageList = useCallback((imageList, logos) => {
    if (imageAssetsOptional || logos) {
      return [];
    }

    if (!isMeetingMinimumRequirementPerAspectRatio(imageList, logos)) {
      let error = logos ?
        i18n.getString(_TL_('You need to add at least 1 logo with 1:1 aspect ratio to your asset group.')) :
        i18n.getString(_TL_('You need to add at least 1 image with 1.91:1 aspect ratio and 1 image with 1:1 aspect ratio to your asset group.'));

      if (isAd) {
        error = logos ?
          i18n.getString(_TL_('You need to add at least 1 logo with 1:1 aspect ratio to the ad.')) :
          i18n.getString(_TL_('You need to add at least 1 image with 1.91:1 aspect ratio and 1 image with 1:1 aspect ratio to the ad.'));
      }

      if (isMMAV2Enabled) {
        error = logos ?
          i18n.getString(_TL_('You need to add at least 1 logo to the ad.')) :
          i18n.getString(_TL_('You need to add at least 1 image to the ad with minimum requirement.'));
      }

      if (isLinkedInAd) {
        error = i18n.getString(_TL_('You need to add 1 image to the ad.'));
      }

      return [error];
    }

    return [];
  }, [imageAssetsOptional, isMeetingMinimumRequirementPerAspectRatio, i18n, isAd, isMMAV2Enabled]);

  const updateFinalUrlStateFromWizardIfChanged = useCallback((vmAssetsInfo) => {
    const vmFinalUrl = vmAssetsInfo.finalUrl;
    if (vmFinalUrl !== finalUrlState.wizardFinalUrl) {
      setFinalUrlState({
        wizardFinalUrl: vmFinalUrl,
        ...getInternalFinalUrlStateFromUrl(vmFinalUrl),
      });
    }
  }, [finalUrlState]);

  const onImageSelected = useCallback((imageId, isLogoSelected = false) => {
    if (isLogoSelected) {
      setState({
        ...state,
        currentSelectedLogoId: imageId,
      });

      setPreviewImagesSharedState({
        ...previewImagesSharedState, previewLogoId: imageId,
      });
    } else {
      setState({
        ...state,
        currentSelectedImageId: imageId,
      });

      setPreviewImagesSharedState({
        ...previewImagesSharedState, cropImageObj: null, previewImageId: imageId, editingPlacementType: '',
      });
    }
  }, [state, setPreviewImagesSharedState, previewImagesSharedState]);

  const onImageRemoved = useCallback((imageId, updatedImageList, isLogo) => {
    if ((isLogo && `${state.currentSelectedLogoId}` === `${imageId}`) || (!isLogo && `${state.currentSelectedImageId}` === `${imageId}`)) {
      const selectedImageId = getDefaultSelectedImageId(updatedImageList, isLogo);
      onImageSelected(selectedImageId, isLogo);
    }
    if (!copilotTracking || !copilotTracking.isInRemainingInitialSuggestions(AssetPropertyName.IMAGE, imageId)) { return; }
    copilotTracking.removeInitialSuggestion(AssetPropertyName.IMAGE, imageId);
  }, [copilotTracking, onImageSelected, state.currentSelectedImageId, state.currentSelectedLogoId]);

  const vmImageAssets = getImageAssetsFromVm(validationProps.data, imageListPropName);
  const vmImageList = getImageListFromVm(validationProps.data, imageListPropName);
  const latestImageList = useRef();

  const getNewlyAddedImageId = useCallback((latest, updated) => {
    const newImage = _.find(updated, image => !_.contains(latest, image));
    if (_.isArray(newImage)) {
      const { id } = _.first(newImage);
      return id;
    }
    return null;
  }, []);

  const onImageListUpdated = useCallback((updatedImageList, currentSelectedImageId, isLogo, shouldDirty = true) => {
    setPreviewImagesSharedState(prevState => ({ ...prevState, cropImageObj: null, editingPlacementType: '' }));
    const imagesErrorList = [...validateImageList(updatedImageList, false)];
    const logosErrorList = [...validateImageList(updatedImageList, true)];
    const allErrors = [...imagesErrorList, ...logosErrorList];

    const listSizeChange = _.size(updatedImageList) - _.size(latestImageList.current);

    updatePortalContainerState({
      imageErrors: imagesErrorList,
      logoErrors: logosErrorList,
      isDirty: shouldDirty,
      imageListSizeChange: listSizeChange,
    });

    updateImageListCallback(updatedImageList);
    if (customAssetLibraryEventHandler && customAssetLibraryEventHandler.onImageListUpdate) {
      customAssetLibraryEventHandler.onImageListUpdate(activity, updatedImageList, latestImageList.current);
    }

    if (suggestedImageBeingAdded.current) {
      const addedImageId = getNewlyAddedImageId(latestImageList.current, updatedImageList);
      if (addedImageId) {
        const { suggestionId } = suggestedImageBeingAdded.current;
        reportRecommendations({ selectedImageSuggestions: [{ suggestionId, imageId: addedImageId }] });
      }
      suggestedImageBeingAdded.current = null;
    }

    latestImageList.current = updatedImageList;
    updateValidation(allErrors, createImagesVm({ imageList: updatedImageList, imageListPropName }, shouldDirty));

    if (currentSelectedImageId) {
      onImageSelected(currentSelectedImageId, isLogo);
    }

    if (activity) {
      activity.signal(`onImageListUpdated - currentImageSize: ${JSON.stringify(updatedImageList)}`);
    }
  }, [setPreviewImagesSharedState, validateImageList, updatePortalContainerState, updateImageListCallback, customAssetLibraryEventHandler, updateValidation, imageListPropName, activity, getNewlyAddedImageId, reportRecommendations, onImageSelected]);

  useEffect(() => {
    if (latestImageList.current !== vmImageList) {
      // This trigger image list validate that is not triggered by image experience udating image list:
      // 1. On first run after render - we should not mark dirty in this case
      // 2. When image list is updated externally by ad-preview - we should mark dirty in this case
      onImageListUpdated(vmImageList, null, null, previewImagesSharedState.isImageListUpdatedByPreview /* shouldDirty */);
    }
  }, [onImageListUpdated, previewImagesSharedState.isImageListUpdatedByPreview, vmImageList]);

  const asyncTokenRef = useRef();

  useEffect(() => {
    if (vmImageAssets && vmImageAssets.length > 0) {
      if (asyncTokenRef.current != null) {
        asyncTokenRef.current.cancel();
      }
      updatePortalContainerState({ imageAssetsLoadPending: true });
      asyncTokenRef.current = processImageListModelToViewModel(vmImageAssets, scope.values.cid, scope.values.aid, {
        isCombinedMsanAndMmaAspectRatios: !isMultimediaAd,
        isLinkedInAd,
        isPMaxAspectRatiosV2Enabled,
        isInlineAssetEditorRefreshEnabled: props.isInlineAssetEditorRefreshEnabled,
      })
        .on({
          onSuccess: (loadedImageList) => {
            let selectedImageId = null;
            if (isMultimediaAd) {
              selectedImageId = getDefaultCurrentSelectedImageId(loadedImageList);
            }
            return onImageListUpdated(loadedImageList, selectedImageId, null, false /* shouldDirty */);
          },
          onError: () => {
            const errorArray = ['Image asset load failure'];
            updatePortalContainerState({
              imageErrors: errorArray,
              logoErrors: errorArray,
              imageAssetsLoadFailure: true,
            });
            updateValidation(errorArray, createImagesVm({ imageList: null, imageListPropName }));
          },
        });
    } else {
      onImageListUpdated(vmImageList, null, null, false /* shouldDirty */);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onImageListUpdated(vmImageList, null, null, false /* shouldDirty */);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageAssetsOptional]);

  const onImageEditCancelled = useCallback(() => {
    setPreviewImagesSharedState({ ...previewImagesSharedState, cropImageObj: null, editingPlacementType: '' });
  }, [previewImagesSharedState, setPreviewImagesSharedState]);

  const onImagePreviewRequested = () => {
  };

  const onSaveThumbnailAsDraft = () => {
  };

  const vmAssetsInfo = getAssetsFromVm(validationProps.data);
  updateFinalUrlStateFromWizardIfChanged(vmAssetsInfo);

  const updateFinalUrl = useCallback((newFinalUrl) => {
    // if url is valid then trigger the wizard to update the final url which will trigger this component to update entire state in updateFinalUrlStateFromWizardIfChanged
    // if its not valid, just update internal state
    if (_.isEmpty(validateAdUrl(newFinalUrl, '', i18n))) {
      updateFinalUrlCallback([newFinalUrl]);
    } else {
      setFinalUrlState({
        ...finalUrlState,
        ...getInternalFinalUrlStateFromUrl(newFinalUrl),
      });
    }
  }, [updateFinalUrlCallback, i18n, finalUrlState]);

  const onUpdateFinalUrlPrefix = useCallback((ev, newPrefix) => {
    const prefixText = newPrefix.text;
    const newFinalUrl = prefixText + finalUrlState.internalFinalUrlDest;
    updateFinalUrl(newFinalUrl);
  }, [finalUrlState.internalFinalUrlDest, updateFinalUrl]);

  const onUpdateFinalUrl = useCallback((ev) => {
    const newUrl = ev && ev.target && ev.target.value;
    const newFinalUrl = finalUrlState.internalFinalUrlProtocol + newUrl;
    updateFinalUrl(newFinalUrl);
  }, [finalUrlState.internalFinalUrlProtocol, updateFinalUrl]);

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  const legacyI18n = useMemo(() => window.globalLegacyI18n.get('$/i18n!legacy/global'), []);
  const i18nMerged = useMemo(() => mergeI18n(legacyI18n, i18n), [legacyI18n, i18n]);

  const mediaRepository = useMemo(() => new MediaRepository({
    cid: scope.values.cid,
    aid: scope.values.aid,
    cmpId: campaignId,
    oDataConfig,
  }), [scope.values.cid, scope.values.aid, campaignId, oDataConfig]);

  const assetLibraryEventHandler = useAssetLibraryEventHandler(postAssetEventLog);
  const finalAssetLibraryEventHandler = useMemo(
    () => ({
      ...assetLibraryEventHandler,
      onUploadImage: (imgId) => {
        if (customAssetLibraryEventHandler && customAssetLibraryEventHandler.onUploadImage) {
          customAssetLibraryEventHandler.onUploadImage(activity, imgId);
        }
      },
    }),
    [activity, assetLibraryEventHandler, customAssetLibraryEventHandler]
  );

  const perfMarker = wizardPerfMarker.createChild('PerfMaxImageExperienceWrapper', { autoEnd: true });
  return (
    <div className={classes.imageExperienceContainer}>
      <ImageExperience
        i18n={i18nMerged}
        newI18n={newI18n}
        previewTexts={previewTexts}
        cid={scope.values.cid}
        aid={scope.values.aid}
        odata={odata}
        oDataConfig={oDataConfig}
        perfMarker={perfMarker}
        activityLogger={activity}
        permissions={permissions}
        imageList={vmImageList}
        shortHeadline={vmAssetsInfo.shortHeadline}
        longHeadline={vmAssetsInfo.longHeadline}
        description={vmAssetsInfo.description}
        businessName={vmAssetsInfo.businessName}
        url={vmAssetsInfo.displayUrl}
        callToAction={vmAssetsInfo.callToAction}
        finalUrlProtocol={finalUrlState.internalFinalUrlProtocol}
        finalUrl={finalUrlState.internalFinalUrl}
        dateFormat={_.get(serverContext, ['CurrentUser', 'CultureDateTimeFormat', 'ShortDatePattern'])}
        timeZone={_.get(serverContext, ['CurrentAccount', 'TimeZone'])}
        customThumbnailRecord={state.thumbnailRecord}
        currentSelectedImageId={state.currentSelectedImageId}
        currentSelectedLogoId={state.currentSelectedLogoId}
        onImageEditCancelled={onImageEditCancelled}
        onImageSelected={onImageSelected}
        onImageRemoved={onImageRemoved}
        onImageListUpdated={onImageListUpdated}
        onImagePreviewRequested={onImagePreviewRequested}
        onSaveThumbnailAsDraft={onSaveThumbnailAsDraft}
        onUpdateFinalUrlPrefix={onUpdateFinalUrlPrefix}
        onUpdateFinalUrl={onUpdateFinalUrl}
        isForVNext
        isAudienceAd={isAudienceAd}
        isMultimediaAdinMSANEnabled={isMultimediaAdinMSANEnabled}
        isMultimediaAd={isMultimediaAd}
        isMMAV2Enabled={isMMAV2Enabled}
        isCombinedMsanAndMmaAspectRatios={isCombinedMsanAndMmaAspectRatios === undefined ? true : isCombinedMsanAndMmaAspectRatios}
        isStockPhotoEnabled
        isVideo={false}
        isBoostPortraitEnabled={isBoostPortraitEnabled}
        cropImage={previewImagesSharedState.cropImageObj}
        suggestion={recommendationContext && recommendationContext.images && recommendationContext.images.websiteImagesState && recommendationContext.images.websiteImagesState.suggestion}
        suggestionPoller={suggestionPoller}
        isAdCreatorEnabled
        isFinalUrlImageRetrievalEnabled={false}
        isLogoEnabled={isLogoEnabled}
        mediaRepository={mediaRepository}
        setAddNewImageCallback={isCopilotRecommendations ? _.noop : (callback) => { imageExperienceAddImageCalbackRef.current = callback; }}
        setAddNewImagesCallback={isCopilotRecommendations ? (callback) => { imageExperienceAddImageCalbackRef.current = callback; } : _.noop}
        setOpenAddImageCallback={_.noop}
        adCreatorScenario={instrumentationScenarios.default}
        onManualCropLogo={() => setState({ ...state, hasManualCroppedLogo: true })}
        onManualCropImage={() => setState({ ...state, hasManualCroppedImage: true })}
        preferencesService={preferenceService}
        imagesPortalContainer={imagesContaineRef.current}
        logosPortalContainer={isLogoEnabled && logosContaineRef.current}
        portraitPortalContainer={portraitContainerRef && portraitContainerRef.current}
        isRenderAddInImageList
        customMaxImageCount={customMaxImageCount || PMaxImageExperienceMaxImageCount}
        showCopilotOptionForImage={showCopilotOptionForImage}
        postAssetEventLog={postAssetEventLog}
        assetLibraryEventHandler={finalAssetLibraryEventHandler}
        editingPlacementType={previewImagesSharedState.editingPlacementType}
        adPreviewConfig={adPreviewConfig}
        enableUnifiedAdPreview={UAPLPMaxIntegration}
        isMSANBoostEnabled={isMSANBoostEnabled}
        isBulkEditMMA={isBulkEditMMA}
        isImproveMMA={isImproveMMA}
        genericImage={genericImage}
        isStockImageEnabled={isStockImageEnabled}
        isStoreAppProng={isStoreAppProng}
        isStoreAppSpotLight={isStoreAppSpotLight}
        isStoreAppTransparentLogo={isStoreAppTransparentLogo}
        enableImageListEdit={!isStoreAppProng && !isStoreAppSpotLight && !isStoreAppTransparentLogo}
        isUnifiedAppCampaignWindowsStore={isUnifiedAppCampaignWindowsStore}
        isLinkedInAd={isLinkedInAd}
        customPreviewTexts={customPreviewTexts}
        customOnEditImage={customOnEditImage}
        onChangeEditImageId={onChangeEditImageId}
        customOnSaveEditImage={customOnSaveEditImage}
        onRenderAllowedAspectRatioControl={onRenderAllowedAspectRatioControl}
        supportImageUpscale={supportImageUpscale}
        isImageFittingEnabled={isImageFittingEnabled}
        isPMaxAspectRatiosV2Enabled={isPMaxAspectRatiosV2Enabled}
        onToggleAspectRatio={onToggleAspectRatio}
        canExcludeAspectRatio={canExcludeAspectRatio}
        includedTypeList={includedTypeList}
        useNewInlineAssetEditor={props.isInlineAssetEditorRefreshEnabled}
      />
    </div>
  );
};

ImageExperienceWrapperInternal.propTypes = {
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      Images: PropTypes.shape({}),
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      DisplayPlusCopilotRecommendations: PropTypes.bool,
    }),
  }).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  instrumentation: PropTypes.shape({}).isRequired,
  updateFinalUrlCallback: PropTypes.func.isRequired,
  imagesContaineRef: PropTypes.shape({
    current: PropTypes.shape({}),
  }).isRequired,
  logosContaineRef: PropTypes.shape({
    current: PropTypes.shape({}),
  }).isRequired,
  portraitContainerRef: PropTypes.shape({
    current: PropTypes.shape({}),
  }),
  isBoostPortraitEnabled: PropTypes.bool,
  campaignId: PropTypes.string,
  imageAssetsOptional: PropTypes.bool,
  isLogoEnabled: PropTypes.bool,
  previewImagesSharedState: PropTypes.shape({
    cropImageObj: PropTypes.shape({}),
    isImageListUpdatedByPreview: PropTypes.bool,
    editingPlacementType: PropTypes.string,
  }).isRequired,
  setPreviewImagesSharedState: PropTypes.func.isRequired,
  updatePortalContainerState: PropTypes.func.isRequired,
  suggestionPoller: PropTypes.shape({
    getLandingPageSuggestions: PropTypes.func.isRequired,
    cancel: PropTypes.func.isRequired,
  }).isRequired,
  setImageExperienceAddNewImageCallback: PropTypes.func,
  wizardPerfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  postAssetEventLog: PropTypes.func,
  isCombinedMsanAndMmaAspectRatios: PropTypes.bool,
  customMaxImageCount: PropTypes.number,
  UAPLPMaxIntegration: PropTypes.bool,
  isMSANBoostEnabled: PropTypes.bool,
  imageListPropName: PropTypes.string,
  isBulkEditMMA: PropTypes.bool,
  isImproveMMA: PropTypes.bool,
  genericImage: PropTypes.string,
  isMultimediaAd: PropTypes.bool,
  isMultimediaAdinMSANEnabled: PropTypes.bool,
  isCopilotRecommendations: PropTypes.bool,
  adPreviewConfig: PropTypes.shape({
    adType: PropTypes.string,
    placementConfig: PropTypes.arrayOf(PropTypes.shape({})),
    extraPlacementInfo: PropTypes.shape({}),
  }),
  optionalAspectRatio: PropTypes.shape({}),
  updateImageListCallback: PropTypes.func,
  isStoreAppProng: PropTypes.bool,
  isStoreAppSpotLight: PropTypes.bool,
  isAd: PropTypes.bool,
  isStockImageEnabled: PropTypes.bool,
  isStoreAppTransparentLogo: PropTypes.bool,
  isMMAV2Enabled: PropTypes.bool,
  showCopilotOptionForImage: PropTypes.bool,
  customAssetLibraryEventHandler: PropTypes.shape({}),
  isLinkedInAd: PropTypes.bool,
  customPreviewTexts: PropTypes.shape({}),
  customOnEditImage: PropTypes.func,
  onChangeEditImageId: PropTypes.func,
  customOnSaveEditImage: PropTypes.func,
  onRenderAllowedAspectRatioControl: PropTypes.func,
  isAudienceAd: PropTypes.bool,
  supportImageUpscale: PropTypes.bool,
  isImageFittingEnabled: PropTypes.bool,
  isUnifiedAppCampaignWindowsStore: PropTypes.bool,
  isPMaxAspectRatiosV2Enabled: PropTypes.bool,
  isAdPreviewRefreshEnabled: PropTypes.bool,
  onToggleAspectRatio: PropTypes.func,
  canExcludeAspectRatio: PropTypes.func,
  includedTypeList: PropTypes.arrayOf(PropTypes.string),
  isInlineAssetEditorRefreshEnabled: PropTypes.bool,
};

ImageExperienceWrapperInternal.defaultProps = {
  campaignId: null,
  imageAssetsOptional: false,
  isLogoEnabled: true,
  postAssetEventLog: _.noop,
  customMaxImageCount: null,
  portraitContainerRef: null,
  isBoostPortraitEnabled: false,
  UAPLPMaxIntegration: false,
  isMSANBoostEnabled: false,
  imageListPropName: null,
  setImageExperienceAddNewImageCallback: _.noop,
  isBulkEditMMA: false,
  isImproveMMA: false,
  genericImage: null,
  isMultimediaAd: false,
  isMultimediaAdinMSANEnabled: true,
  isCopilotRecommendations: true,
  adPreviewConfig: {
    adType: AdTypeEnum.pMax,
    placementConfig: undefined,
    extraPlacementInfo: undefined,
  },
  optionalAspectRatio: null,
  updateImageListCallback: _.noop,
  isStoreAppProng: false,
  isStoreAppSpotLight: false,
  isAd: false,
  isStockImageEnabled: true,
  isStoreAppTransparentLogo: false,
  isMMAV2Enabled: false,
  showCopilotOptionForImage: false,
  customAssetLibraryEventHandler: null,
  isLinkedInAd: false,
  customPreviewTexts: undefined,
  customOnEditImage: null,
  onChangeEditImageId: _.noop,
  customOnSaveEditImage: null,
  onRenderAllowedAspectRatioControl: null,
  isAudienceAd: false,
  supportImageUpscale: false,
  isImageFittingEnabled: false,
  isUnifiedAppCampaignWindowsStore: false,
  isPMaxAspectRatiosV2Enabled: false,
  isAdPreviewRefreshEnabled: false,
  onToggleAspectRatio: null,
  canExcludeAspectRatio: null,
  includedTypeList: [],
  isInlineAssetEditorRefreshEnabled: false,
};

export const ValidatedImageExperienceWrapper = withInstrumentation(withPreference(withCustomValidation(ImageExperienceWrapperInternal)));
